import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authLogger } from '../../utils/logger';
import { LoadingPage } from '../ui/LoadingSpinner';
import { hasMinimumAdminAccess, hasRole, MINIMUM_ADMIN_ROLE } from '../../types/roles';
import AdminLoginPage from './AdminLoginPage';
import { ShieldAlert, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
  /** Required role(s) for this route. If not specified, only minimum admin access is checked. */
  requiredRole?: string;
}

/**
 * Protected route component for admin pages.
 * - Shows AdminLoginPage if not authenticated
 * - Shows Access Denied if authenticated but missing required role
 * - No onboarding check (admins don't need onboarding)
 */
const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ 
  children, 
  requiredRole
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const [msalInitialized, setMsalInitialized] = useState(false);

  // Track MSAL initialization status
  useEffect(() => {
    const checkInitialization = (): boolean => {
      const initialized = !!window.__MSAL_INITIALIZED__;
      setMsalInitialized(initialized);
      return initialized;
    };

    if (checkInitialization()) {
      return;
    }

    const handleMsalInitialized = (): void => {
      authLogger.debug('AdminProtectedRoute received MSAL initialization event');
      setMsalInitialized(true);
    };

    window.addEventListener('msalInitialized', handleMsalInitialized);

    return () => {
      window.removeEventListener('msalInitialized', handleMsalInitialized);
    };
  }, []);

  // Show loading spinner while checking authentication or MSAL initialization
  if (isLoading || !msalInitialized) {
    authLogger.debug('AdminProtectedRoute showing loading spinner', { isLoading, msalInitialized });
    return <LoadingPage />;
  }

  // Show admin login page if not authenticated
  if (!isAuthenticated) {
    authLogger.debug('AdminProtectedRoute showing admin login - not authenticated');
    return <AdminLoginPage />;
  }

  // Check minimum admin access (reportsReader role)
  if (!hasMinimumAdminAccess(user?.roles)) {
    authLogger.debug('AdminProtectedRoute access denied - missing minimum admin role', { 
      userRoles: user?.roles,
      requiredRole: MINIMUM_ADMIN_ROLE 
    });
    return <AccessDeniedPage missingRole={MINIMUM_ADMIN_ROLE} isMinimumAccess />;
  }

  // Check specific required role if specified
  if (requiredRole && !hasRole(user?.roles, requiredRole)) {
    authLogger.debug('AdminProtectedRoute access denied - missing required role', { 
      userRoles: user?.roles,
      requiredRole 
    });
    return <AccessDeniedPage missingRole={requiredRole} />;
  }

  authLogger.debug('AdminProtectedRoute allowing access to admin content');
  return <>{children}</>;
};

interface AccessDeniedPageProps {
  missingRole: string;
  isMinimumAccess?: boolean;
}

const AccessDeniedPage: React.FC<AccessDeniedPageProps> = ({ missingRole, isMinimumAccess }) => {
  const roleDisplayNames: Record<string, string> = {
    reportsReader: 'Reports Reader',
    sessionsReader: 'Sessions Reader',
    testContributor: 'Test Contributor',
    feedbacksReader: 'Feedbacks Reader',
    admin: 'Administrator',
  };

  const roleName = roleDisplayNames[missingRole] || missingRole;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rm-primary-50 via-rm-bg-0 to-rm-primary-100 py-12 px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-rm-ink-900 mb-2">
            Access Denied
          </h1>
          
          {isMinimumAccess ? (
            <>
              <p className="text-rm-neutral-600 mb-6">
                You don't have permission to access the Admin Portal. 
                The <strong>{roleName}</strong> role is required for access.
              </p>
              <p className="text-sm text-rm-neutral-500 mb-6">
                Please contact your administrator to request access.
              </p>
            </>
          ) : (
            <>
              <p className="text-rm-neutral-600 mb-6">
                You don't have permission to access this page. 
                The <strong>{roleName}</strong> role is required.
              </p>
              <Link 
                to="/admin/reports"
                className="inline-flex items-center text-rm-primary-600 hover:text-rm-primary-700 font-medium mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go to Reports
              </Link>
            </>
          )}
          
          <div className="pt-4 border-t border-rm-neutral-200">
            <Link 
              to="/"
              className="text-sm text-rm-primary-600 hover:text-rm-primary-700 font-medium transition-colors"
            >
              ← Go to Wellbeing App
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProtectedRoute;
